import { gtag } from '@/src/configs/gtag'
import { AnalyticsEvent, AnalyticsPageType } from '@/src/googleAnalytics/enums'
import { useEffect, useCallback } from 'react'
import { gtagConfig } from './config'
import { ROUTES } from '@/src/utils/routes'

interface GtagEventData {
  event: string
  page?: string
  title?: string
  page_type?: string
  [key: string]: any
}

export const sendGtagEvent = (data: GtagEventData): void => {
  if (typeof window === 'undefined') {
    return
  }

  if (!window.gtag) {
    if (gtagConfig.debugMode) {
      // Gtag not available
    }

    return
  }

  if (!gtagConfig.shouldTrack) {
    if (gtagConfig.debugMode) {
      // Development logging disabled
    }

    return
  }

  try {
    if (data.event === AnalyticsEvent.PAGE_VIEW) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { event, page_type, title, page, ...otherData } = data

      gtag('event', 'page_view', {
        page_title: title,
        page_location: page,
        page_type,
        ...otherData
      })
    } else {
      const { event, ...eventData } = data
      gtag('event', event, eventData)
    }

    if (gtagConfig.debugMode) {
      // Development logging disabled
    }
  } catch (error) {
    // Error handling without console logging
  }
}

export const AnalyticsTracker = {
  trackCustomerPortalDashboard: () => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.CUSTOMER_PORTAL,
      title: 'Guest Experience Dashboard',
      page_type: AnalyticsPageType.CUSTOMER_PORTAL_DASHBOARD
    })
  },

  trackCustomerPortalOrderDetails: (orderId: string) => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.CUSTOMER_PORTAL_ORDER_DETAIL(orderId),
      title: 'Order Details',
      page_type: AnalyticsPageType.CUSTOMER_PORTAL_ORDER_DETAILS,
      orderId
    })
  },

  trackCustomerPortalUserDetails: (userId: string) => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.CUSTOMER_PORTAL_USER_DETAIL(userId),
      title: 'User Details',
      page_type: AnalyticsPageType.CUSTOMER_PORTAL_USER_DETAILS,
      userId
    })
  },

  // Marketing Portal Tracking
  trackMarketingPortalDashboard: () => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.MARKETING_PORTAL,
      title: 'Marketing Portal',
      page_type: AnalyticsPageType.MARKETING_PORTAL_DASHBOARD
    })
  },

  trackMarketingPortalOfferDetails: (offerId: string) => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.MARKETING_PORTAL_OFFERS_DETAIL(offerId),
      title: 'Offer Details',
      page_type: AnalyticsPageType.MARKETING_PORTAL_OFFER_DETAILS,
      offerId
    })
  },

  trackRestaurantList: () => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.RESTAURANT,
      title: 'Master Restaurant List',
      page_type: AnalyticsPageType.RESTAURANT_LIST
    })
  },

  trackRestaurantDetails: (restaurantId: string, restaurantName?: string) => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.RESTAURANT_DETAIL(restaurantId),
      title: 'Restaurant Details',
      page_type: AnalyticsPageType.RESTAURANT_DETAILS,
      restaurantId,
      ...(restaurantName && { restaurantName })
    })
  },

  trackRestaurantEquipment: (restaurantId: string) => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.RESTAURANT_EQUIPMENT(restaurantId),
      title: 'Restaurant Equipment',
      page_type: AnalyticsPageType.RESTAURANT_EQUIPMENT,
      restaurantId
    })
  },

  trackRestaurantReadyToRoll: (restaurantId: string) => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.RESTAURANT_READY_TO_ROLL(restaurantId),
      title: 'Ready to Roll',
      page_type: AnalyticsPageType.RESTAURANT_READY_TO_ROLL,
      restaurantId
    })
  },

  trackRestaurantReadyToRollAdd: (restaurantId: string) => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.RESTAURANT_READY_TO_ROLL_ADD(restaurantId),
      title: 'Add Ready to Roll',
      page_type: AnalyticsPageType.RESTAURANT_READY_TO_ROLL_ADD,
      restaurantId
    })
  },

  trackRestaurantReadyToRollView: (restaurantId: string, slug: string) => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.RESTAURANT_READY_TO_ROLL_VIEW(restaurantId, slug),
      title: 'Ready to Roll View',
      page_type: AnalyticsPageType.RESTAURANT_READY_TO_ROLL_VIEW,
      restaurantId,
      slug
    })
  },

  trackRestaurantReadyToRollReport: (restaurantId: string) => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.RESTAURANT_READY_TO_ROLL_REPORT(restaurantId),
      title: 'Ready to Roll Report',
      page_type: AnalyticsPageType.RESTAURANT_READY_TO_ROLL_REPORT,
      restaurantId
    })
  },

  trackFranchiseHubList: () => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.FRANCHISE_HUB,
      title: 'Franchise List',
      page_type: AnalyticsPageType.FRANCHISE_HUB_LIST
    })
  },

  trackFranchiseHubAdd: () => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.FRANCHISE_HUB_ADD_BASE,
      title: 'Add Franchise Review',
      page_type: AnalyticsPageType.FRANCHISE_HUB_ADD
    })
  },

  trackFranchiseHubDetails: (franchiseId: string) => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.FRANCHISE_HUB_DETAIL(franchiseId),
      title: 'Franchise Details',
      page_type: AnalyticsPageType.FRANCHISE_HUB_DETAILS,
      franchiseId
    })
  },

  trackFranchiseHubReviewSignoff: (reviewId: string) => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.FRANCHISE_HUB_REVIEW_SIGNOFF(reviewId),
      title: 'Franchise Review Signoff',
      page_type: AnalyticsPageType.FRANCHISE_HUB_REVIEW_SIGNOFF,
      reviewId
    })
  },

  trackFranchiseHubReviewSubmission: (reviewId: string) => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.FRANCHISE_HUB_REVIEW_SUBMISSION(reviewId),
      title: 'Franchise Review Submission',
      page_type: AnalyticsPageType.FRANCHISE_HUB_REVIEW_SUBMISSION,
      reviewId
    })
  },

  trackUserManagementDashboard: () => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.USER_MANAGEMENT,
      title: 'User Role Management',
      page_type: AnalyticsPageType.USER_MANAGEMENT_DASHBOARD
    })
  },

  trackUserManagementRoles: () => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.USER_MANAGEMENT_ROLES,
      title: 'User Roles',
      page_type: AnalyticsPageType.USER_MANAGEMENT_ROLES
    })
  },

  trackUserManagementPermissions: () => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.USER_MANAGEMENT_PERMISSIONS,
      title: 'User Permissions',
      page_type: AnalyticsPageType.USER_MANAGEMENT_PERMISSIONS
    })
  },

  trackSubmissionsDashboard: (selectedOption?: string) => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.SUBMISSIONS,
      title: 'Form Submissions',
      page_type: AnalyticsPageType.SUBMISSIONS_DASHBOARD,
      selected_option: selectedOption
    })
  },

  trackSubmissionsReadyToRoll: () => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.SUBMISSIONS_READY_TO_ROLL,
      title: 'Ready to Roll Submissions',
      page_type: AnalyticsPageType.SUBMISSIONS_READY_TO_ROLL
    })
  },

  trackSubmissionsRSS: () => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.SUBMISSIONS_RSS,
      title: 'RSS Submissions',
      page_type: AnalyticsPageType.SUBMISSIONS_RSS
    })
  },

  trackSubmissionsCleaningSheet: () => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.SUBMISSIONS_CLEANING_SHEET,
      title: 'Cleaning Sheet Submissions',
      page_type: AnalyticsPageType.SUBMISSIONS_CLEANING_SHEET
    })
  },

  trackFaqDashboard: () => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.FAQ,
      title: 'FAQ Dashboard',
      page_type: AnalyticsPageType.FAQ_DASHBOARD
    })
  },

  trackFaqCategory: (categoryId: string) => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.FAQ_CATEGORY(categoryId),
      title: 'FAQ Category',
      page_type: AnalyticsPageType.FAQ_CATEGORY,
      categoryId
    })
  },

  trackFaqDetails: (faqId: string) => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.FAQ_DETAIL(faqId),
      title: 'FAQ Details',
      page_type: AnalyticsPageType.FAQ_DETAILS,
      faqId
    })
  },

  trackHistoryLogs: () => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.HISTORY_LOGS,
      title: 'History Logs',
      page_type: AnalyticsPageType.HISTORY_LOGS
    })
  },

  trackHistoryLogsDetails: (logId: string) => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.HISTORY_LOGS_DETAIL(logId),
      title: 'History Log Details',
      page_type: AnalyticsPageType.HISTORY_LOGS_DETAILS,
      logId
    })
  },

  trackDisabledIngredients: () => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.DISABLED_INGREDIENTS,
      title: 'Disabled Ingredients',
      page_type: AnalyticsPageType.DISABLED_INGREDIENTS
    })
  },

  trackDisabledIngredientsAdd: () => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.DISABLED_INGREDIENTS_ADD,
      title: 'Add Disabled Ingredient',
      page_type: AnalyticsPageType.DISABLED_INGREDIENTS_ADD
    })
  },

  trackDisabledIngredientsEdit: (ingredientId: string) => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.DISABLED_INGREDIENTS_EDIT(ingredientId),
      title: 'Edit Disabled Ingredient',
      page_type: AnalyticsPageType.DISABLED_INGREDIENTS_EDIT,
      ingredientId
    })
  },

  trackProductsList: (title?: string) => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.PRODUCTS,
      title: title || 'Products',
      page_type: AnalyticsPageType.PRODUCTS_LIST
    })
  },

  trackProductsDetails: (productId: string) => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.PRODUCTS_DETAIL(productId),
      title: 'Product Details',
      page_type: AnalyticsPageType.PRODUCTS_DETAILS,
      productId
    })
  },

  trackProductsPricing: () => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.PRODUCTS_PRICING,
      title: 'Product Pricing',
      page_type: AnalyticsPageType.PRODUCTS_PRICING
    })
  },

  trackLogin: () => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.LOGIN,
      title: 'Login',
      page_type: AnalyticsPageType.LOGIN
    })
  },

  trackRegister: () => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.REGISTER,
      title: 'Register',
      page_type: AnalyticsPageType.REGISTER
    })
  },

  trackForgotPassword: () => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.FORGOT_PASSWORD,
      title: 'Forgot Password',
      page_type: AnalyticsPageType.FORGOT_PASSWORD
    })
  },

  trackResetPassword: () => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.RESET_PASSWORD,
      title: 'Reset Password',
      page_type: AnalyticsPageType.RESET_PASSWORD
    })
  },

  trackError404: () => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.NOT_FOUND,
      title: 'Page Not Found',
      page_type: AnalyticsPageType.ERROR_404
    })
  },

  trackError401: () => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.UNAUTHORIZED,
      title: 'Unauthorized',
      page_type: AnalyticsPageType.ERROR_401
    })
  },

  trackError500: () => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.SERVER_ERROR,
      title: 'Server Error',
      page_type: AnalyticsPageType.ERROR_500
    })
  },

  trackCustomPage: (page: string, title: string, page_type: string, additionalData?: Record<string, any>) => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page,
      title,
      page_type: page_type,
      ...additionalData
    })
  },

  trackRestaurantAdd: () => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.RESTAURANT_ADD,
      title: 'Add Restaurant',
      page_type: AnalyticsPageType.RESTAURANT_ADD
    })
  },

  trackOrderManagementDashboard: () => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.ORDER_MANAGEMENT,
      title: 'Order Management Dashboard',
      page_type: AnalyticsPageType.ORDER_MANAGEMENT_DASHBOARD
    })
  },

  trackOperationsStoreSurcharges: () => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.OPERATIONS_STORE_SURCHARGES,
      title: 'Store Surcharges',
      page_type: AnalyticsPageType.OPERATIONS_STORE_SURCHARGES
    })
  },

  trackOperationsMasterPricingList: () => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.OPERATIONS_MASTER_PRICING_LIST,
      title: 'Master Pricing List',
      page_type: AnalyticsPageType.OPERATIONS_MASTER_PRICING_LIST
    })
  },

  // Trading Hours Tracking
  trackRestaurantTradingHours: (restaurantId: string, restaurantName?: string) => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.RESTAURANT_TRADING_HOURS(restaurantId),
      title: 'Trading Hours',
      page_type: AnalyticsPageType.RESTAURANT_TRADING_HOURS,
      restaurantId,
      ...(restaurantName && { restaurantName })
    })
  },

  trackTradingHoursUpdate: (
    restaurantId: string,
    tradingHoursType: 'permanent' | 'temporary',
    additionalData?: Record<string, any>
  ) => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.RESTAURANT_TRADING_HOURS(restaurantId),
      title: 'Update Trading Hours',
      page_type: AnalyticsPageType.RESTAURANT_TRADING_HOURS_UPDATE,
      restaurantId,
      tradingHoursType,
      ...additionalData
    })
  },

  trackTradingHoursTemporary: (restaurantId: string, additionalData?: Record<string, any>) => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.RESTAURANT_TRADING_HOURS(restaurantId),
      title: 'Temporary Trading Hours',
      page_type: AnalyticsPageType.RESTAURANT_TRADING_HOURS_TEMPORARY,
      restaurantId,
      ...additionalData
    })
  },

  trackTradingHoursPermanent: (restaurantId: string, additionalData?: Record<string, any>) => {
    sendGtagEvent({
      event: AnalyticsEvent.PAGE_VIEW,
      page: ROUTES.RESTAURANT_TRADING_HOURS(restaurantId),
      title: 'Permanent Trading Hours',
      page_type: AnalyticsPageType.RESTAURANT_TRADING_HOURS_PERMANENT,
      restaurantId,
      ...additionalData
    })
  }
}

export const useAnalytics = () => {
  return AnalyticsTracker
}

export const getPageTypeFromUrl = (url: string): string => {
  const path = url.toLowerCase()

  if (path.includes('/customer-portal')) {
    if (path.includes('/orders/')) {
      return AnalyticsPageType.CUSTOMER_PORTAL_ORDER_DETAILS
    }
    if (path.includes('/users/')) {
      return AnalyticsPageType.CUSTOMER_PORTAL_USER_DETAILS
    }

    return AnalyticsPageType.CUSTOMER_PORTAL_DASHBOARD
  }

  if (path.includes('/marketing-portal')) {
    if (path.includes('/offers/')) {
      return AnalyticsPageType.MARKETING_PORTAL_OFFER_DETAILS
    }

    return AnalyticsPageType.MARKETING_PORTAL_DASHBOARD
  }

  if (path.includes('/restaurant/')) {
    if (path.includes('/equipment')) {
      return AnalyticsPageType.RESTAURANT_EQUIPMENT
    }
    if (path.includes('/ready-to-roll/add')) {
      return AnalyticsPageType.RESTAURANT_READY_TO_ROLL_ADD
    }
    if (path.includes('/ready-to-roll/view/')) {
      return AnalyticsPageType.RESTAURANT_READY_TO_ROLL_VIEW
    }
    if (path.includes('/report-ready-to-roll')) {
      return AnalyticsPageType.RESTAURANT_READY_TO_ROLL_REPORT
    }
    if (path.includes('/ready-to-roll')) {
      return AnalyticsPageType.RESTAURANT_READY_TO_ROLL
    }
    if (path.includes('/menu-services/trading-hours')) {
      return AnalyticsPageType.RESTAURANT_TRADING_HOURS
    }
    if (path.includes('/restaurant')) {
      return AnalyticsPageType.RESTAURANT_LIST
    }

    return AnalyticsPageType.RESTAURANT_DETAILS
  }

  if (path.includes('/franchise-hub')) {
    if (path.includes('/add')) {
      return AnalyticsPageType.FRANCHISE_HUB_ADD
    }
    if (path.includes('/review-signoff/')) {
      return AnalyticsPageType.FRANCHISE_HUB_REVIEW_SIGNOFF
    }
    if (path.includes('/review-submission/')) {
      return AnalyticsPageType.FRANCHISE_HUB_REVIEW_SUBMISSION
    }
    if (path.match(/\/\d+$/)) {
      return AnalyticsPageType.FRANCHISE_HUB_DETAILS
    }

    return AnalyticsPageType.FRANCHISE_HUB_LIST
  }

  if (path.includes('/user-management')) {
    if (path.includes('/roles')) {
      return AnalyticsPageType.USER_MANAGEMENT_ROLES
    }
    if (path.includes('/permissions')) {
      return AnalyticsPageType.USER_MANAGEMENT_PERMISSIONS
    }

    return AnalyticsPageType.USER_MANAGEMENT_DASHBOARD
  }

  if (path.includes('/submissions')) {
    if (path.includes('/ready-to-roll')) {
      return AnalyticsPageType.SUBMISSIONS_READY_TO_ROLL
    }
    if (path.includes('/rss')) {
      return AnalyticsPageType.SUBMISSIONS_RSS
    }
    if (path.includes('/cleaning-sheet')) {
      return AnalyticsPageType.SUBMISSIONS_CLEANING_SHEET
    }

    return AnalyticsPageType.SUBMISSIONS_DASHBOARD
  }

  if (path.includes('/faq')) {
    if (path.includes('/category/')) {
      return AnalyticsPageType.FAQ_CATEGORY
    }
    if (path.match(/\/\d+$/)) {
      return AnalyticsPageType.FAQ_DETAILS
    }

    return AnalyticsPageType.FAQ_DASHBOARD
  }

  if (path.includes('/history-logs')) {
    if (path.match(/\/\d+$/)) {
      return AnalyticsPageType.HISTORY_LOGS_DETAILS
    }

    return AnalyticsPageType.HISTORY_LOGS
  }

  if (path.includes('/disabled-ingredients')) {
    if (path.includes('/add')) {
      return AnalyticsPageType.DISABLED_INGREDIENTS_ADD
    }
    if (path.includes('/edit/')) {
      return AnalyticsPageType.DISABLED_INGREDIENTS_EDIT
    }

    return AnalyticsPageType.DISABLED_INGREDIENTS
  }

  if (path.includes('/products')) {
    if (path.includes('/pricing')) {
      return AnalyticsPageType.PRODUCTS_PRICING
    }
    if (path.match(/\/\d+$/)) {
      return AnalyticsPageType.PRODUCTS_DETAILS
    }

    return AnalyticsPageType.PRODUCTS_LIST
  }

  // Handle root path - treat as login page
  if (path === '/' || path === '') {
    return AnalyticsPageType.LOGIN
  }

  // Handle authentication pages (including with query params)
  if (path.startsWith('/login')) {
    return AnalyticsPageType.LOGIN
  }
  if (path.startsWith('/register')) {
    return AnalyticsPageType.REGISTER
  }
  if (path.startsWith('/forgot-password')) {
    return AnalyticsPageType.FORGOT_PASSWORD
  }
  if (path.startsWith('/reset-password')) {
    return AnalyticsPageType.RESET_PASSWORD
  }

  // Handle error pages
  if (path === ROUTES.NOT_FOUND) {
    return AnalyticsPageType.ERROR_404
  }
  if (path === ROUTES.UNAUTHORIZED) {
    return AnalyticsPageType.ERROR_401
  }
  if (path === ROUTES.SERVER_ERROR) {
    return AnalyticsPageType.ERROR_500
  }

  // Default unknown pages to login instead of 'unknown_page'
  return AnalyticsPageType.LOGIN
}

// Utility functions for gtag operations - GA4 Best Practices
export const gtagUtils = {
  // Track page view with GA4 best practices
  pageView: (page_title: string, page_location: string, custom_parameters?: Record<string, any>) => {
    if (typeof window !== 'undefined' && gtagConfig.shouldTrack) {
      gtag('event', 'page_view', {
        page_title,
        page_location,
        ...custom_parameters
      })
    }
  },

  // Track custom event with validation
  event: (event_name: string, parameters?: Record<string, any>) => {
    if (typeof window !== 'undefined' && gtagConfig.shouldTrack) {
      gtag('event', event_name, parameters || {})
    }
  },

  // Track user engagement with GA4 standard
  engagement: (engagement_time_msec: number) => {
    if (typeof window !== 'undefined') {
      gtag('event', 'user_engagement', {
        engagement_time_msec
      })
    }
  },

  // Track user login with GA4 standard
  login: (method: string) => {
    if (typeof window !== 'undefined') {
      gtag('event', 'login', {
        method
      })
    }
  },

  // Track user sign up with GA4 standard
  signUp: (method: string) => {
    if (typeof window !== 'undefined') {
      gtag('event', 'sign_up', {
        method
      })
    }
  },

  // Track button clicks with GA4 standard
  buttonClick: (button_name: string, page_location?: string) => {
    if (typeof window !== 'undefined') {
      gtag('event', 'button_click', {
        button_name,
        ...(page_location && { page_location })
      })
    }
  },

  // Track form submissions with GA4 standard
  formSubmit: (form_name: string, page_location?: string) => {
    if (typeof window !== 'undefined') {
      gtag('event', 'form_submit', {
        form_name,
        ...(page_location && { page_location })
      })
    }
  },

  // Track errors with GA4 standard
  error: (error_message: string, error_code?: string, page_location?: string) => {
    if (typeof window !== 'undefined') {
      gtag('event', 'exception', {
        description: error_message,
        ...(error_code && { error_code }),
        ...(page_location && { page_location }),
        fatal: false
      })
    }
  },

  // Track fatal errors with GA4 standard
  fatalError: (error_message: string, error_code?: string, page_location?: string) => {
    if (typeof window !== 'undefined') {
      gtag('event', 'exception', {
        description: error_message,
        ...(error_code && { error_code }),
        ...(page_location && { page_location }),
        fatal: true
      })
    }
  },

  // Auto-track page views based on URL
  autoTrackPageView: (url?: string) => {
    if (typeof window !== 'undefined' && gtagConfig.shouldTrack) {
      const currentUrl = url || window.location.pathname + window.location.search
      const page_type = getPageTypeFromUrl(currentUrl)
      const pageTitle = document.title || 'Unknown Page'

      gtag('event', 'page_view', {
        page_title: pageTitle,
        page_location: currentUrl,
        page_type: page_type
      })
    }
  },

  // Track page load performance
  trackPageLoad: (load_time_ms: number, page_location?: string) => {
    if (typeof window !== 'undefined') {
      gtag('event', 'timing_complete', {
        name: 'load',
        value: load_time_ms,
        ...(page_location && { page_location })
      })
    }
  },

  // Track user interaction timing
  trackInteraction: (interaction_name: string, interaction_time_ms: number, page_location?: string) => {
    if (typeof window !== 'undefined') {
      gtag('event', 'timing_complete', {
        name: interaction_name,
        value: interaction_time_ms,
        ...(page_location && { page_location })
      })
    }
  },

  // Track search functionality
  search: (search_term: string, page_location?: string) => {
    if (typeof window !== 'undefined') {
      gtag('event', 'search', {
        search_term,
        ...(page_location && { page_location })
      })
    }
  },

  // Track file downloads
  fileDownload: (file_name: string, file_type: string, page_location?: string) => {
    if (typeof window !== 'undefined') {
      gtag('event', 'file_download', {
        file_name,
        file_type,
        ...(page_location && { page_location })
      })
    }
  },

  // Track video interactions
  videoStart: (video_title: string, video_duration?: number, page_location?: string) => {
    if (typeof window !== 'undefined') {
      gtag('event', 'video_start', {
        video_title,
        ...(video_duration && { video_duration }),
        ...(page_location && { page_location })
      })
    }
  },

  // Track scroll depth
  scrollDepth: (scroll_percentage: number, page_location?: string) => {
    if (typeof window !== 'undefined') {
      gtag('event', 'scroll', {
        scroll_percentage,
        ...(page_location && { page_location })
      })
    }
  },

  // Trading Hours Event Tracking
  tradingHoursUpdate: (
    restaurant_id: string,
    trading_hours_type: 'permanent' | 'temporary',
    menu_types: string[],
    start_date?: string,
    end_date?: string,
    channel?: string,
    reason?: string,
    page_location?: string
  ) => {
    if (typeof window !== 'undefined') {
      gtag('event', 'trading_hours_update', {
        restaurant_id,
        trading_hours_type,
        menu_types: menu_types.join(','),
        ...(start_date && { start_date }),
        ...(end_date && { end_date }),
        ...(channel && { channel }),
        ...(reason && { reason }),
        ...(page_location && { page_location })
      })
    }
  },

  tradingHoursDelete: (restaurant_id: string, override_id: string, section_name: string, page_location?: string) => {
    if (typeof window !== 'undefined') {
      gtag('event', 'trading_hours_delete', {
        restaurant_id,
        override_id,
        section_name,
        ...(page_location && { page_location })
      })
    }
  },

  tradingHoursView: (restaurant_id: string, restaurant_name?: string, page_location?: string) => {
    if (typeof window !== 'undefined') {
      gtag('event', 'trading_hours_view', {
        restaurant_id,
        ...(restaurant_name && { restaurant_name }),
        ...(page_location && { page_location })
      })
    }
  },

  tradingHoursCreate: (
    restaurant_id: string,
    trading_hours_type: 'permanent' | 'temporary',
    menu_types: string[],
    page_location?: string
  ) => {
    if (typeof window !== 'undefined') {
      gtag('event', 'trading_hours_create', {
        restaurant_id,
        trading_hours_type,
        menu_types: menu_types.join(','),
        ...(page_location && { page_location })
      })
    }
  },

  tradingHoursEdit: (restaurant_id: string, override_id: string, section_name: string, page_location?: string) => {
    if (typeof window !== 'undefined') {
      gtag('event', 'trading_hours_edit', {
        restaurant_id,
        override_id,
        section_name,
        ...(page_location && { page_location })
      })
    }
  },

  tradingHoursBulkUpdate: (
    restaurant_ids: string[],
    trading_hours_type: 'permanent' | 'temporary',
    menu_types: string[],
    bulk_count: number,
    page_location?: string
  ) => {
    if (typeof window !== 'undefined') {
      gtag('event', 'trading_hours_bulk_update', {
        restaurant_ids: restaurant_ids.join(','),
        trading_hours_type,
        menu_types: menu_types.join(','),
        bulk_count,
        ...(page_location && { page_location })
      })
    }
  }
}

export const usePageTracking = (pageTitle?: string, customData?: Record<string, any>) => {
  useEffect(() => {
    if (typeof window !== 'undefined' && gtagConfig.shouldTrack) {
      const currentUrl = window.location.pathname + window.location.search
      const page_type = getPageTypeFromUrl(currentUrl)
      const title = pageTitle || document.title || 'Unknown Page'

      gtag('event', 'page_view', {
        page_title: title,
        page_location: currentUrl,
        page_type: page_type,
        ...customData
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTitle]) // Only depend on pageTitle to avoid re-tracking on customData changes
}

export const useEventTracking = () => {
  const trackEvent = useCallback((eventName: string, parameters?: Record<string, any>) => {
    gtagUtils.event(eventName, parameters)
  }, [])

  const trackButtonClick = useCallback((buttonName: string, pageLocation?: string) => {
    gtagUtils.buttonClick(buttonName, pageLocation)
  }, [])

  const trackFormSubmit = useCallback((formName: string, pageLocation?: string) => {
    gtagUtils.formSubmit(formName, pageLocation)
  }, [])

  const trackError = useCallback((errorMessage: string, errorCode?: string, pageLocation?: string) => {
    gtagUtils.error(errorMessage, errorCode, pageLocation)
  }, [])

  const trackTradingHoursUpdate = useCallback(
    (
      restaurantId: string,
      tradingHoursType: 'permanent' | 'temporary',
      menuTypes: string[],
      startDate?: string,
      endDate?: string,
      channel?: string,
      reason?: string,
      pageLocation?: string
    ) => {
      gtagUtils.tradingHoursUpdate(
        restaurantId,
        tradingHoursType,
        menuTypes,
        startDate,
        endDate,
        channel,
        reason,
        pageLocation
      )
    },
    []
  )

  const trackTradingHoursDelete = useCallback(
    (restaurantId: string, overrideId: string, sectionName: string, pageLocation?: string) => {
      gtagUtils.tradingHoursDelete(restaurantId, overrideId, sectionName, pageLocation)
    },
    []
  )

  const trackTradingHoursView = useCallback((restaurantId: string, restaurantName?: string, pageLocation?: string) => {
    gtagUtils.tradingHoursView(restaurantId, restaurantName, pageLocation)
  }, [])

  const trackTradingHoursCreate = useCallback(
    (restaurantId: string, tradingHoursType: 'permanent' | 'temporary', menuTypes: string[], pageLocation?: string) => {
      gtagUtils.tradingHoursCreate(restaurantId, tradingHoursType, menuTypes, pageLocation)
    },
    []
  )

  const trackTradingHoursEdit = useCallback(
    (restaurantId: string, overrideId: string, sectionName: string, pageLocation?: string) => {
      gtagUtils.tradingHoursEdit(restaurantId, overrideId, sectionName, pageLocation)
    },
    []
  )

  const trackTradingHoursBulkUpdate = useCallback(
    (
      restaurantIds: string[],
      tradingHoursType: 'permanent' | 'temporary',
      menuTypes: string[],
      bulkCount: number,
      pageLocation?: string
    ) => {
      gtagUtils.tradingHoursBulkUpdate(restaurantIds, tradingHoursType, menuTypes, bulkCount, pageLocation)
    },
    []
  )

  return {
    trackEvent,
    trackButtonClick,
    trackFormSubmit,
    trackError,
    trackTradingHoursUpdate,
    trackTradingHoursDelete,
    trackTradingHoursView,
    trackTradingHoursCreate,
    trackTradingHoursEdit,
    trackTradingHoursBulkUpdate
  }
}
